/* eslint-disable */
import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import api from "api";
import { useAuth } from "./auth";
import { useEsriUrls } from "./esriUrlsContext";
import getEsriData from "./contextFunctions/getEsriData";
import { NotificationManager } from "react-notifications";
import { ErrorHandelFunctionApp } from "utils/ErrorHandelFunction";

function mutateObjectToWorkWithReactSelect(arr) {
  return arr.map((item) => ({ ...item, value: item.code, label: item.name }));
}

// Auth context
const lookupsContext = createContext();
// Setting custom name for the context which is visible on react dev tools
lookupsContext.displayName = "Lookups Context";

// reducer
function reducer(state, action) {
  switch (action.type) {
    case "SET_DATA": {
      return { ...state, ...action.payload };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// context provider
function LookupsContextProvider({ children }) {
  const initialState = {
    zonesLookup: null,
    usersRoles: null,
  };

  const [auth] = useAuth();
  const [esriUrls] = useEsriUrls();

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  useEffect(() => {
    // self invoked function
    (async () => {
      try {
        if (
          auth.esriToken &&
          ![-1, undefined].includes(esriUrls.Parcels_Intersect)
        ) {
          const parcelsIntersect = getEsriData({
            token: auth.esriToken,
            query: "OBJECTID = 1",
            layerId: esriUrls.Parcels_Intersect,
            layer: process.env.REACT_APP_PetraLayers_Table,
            featureOrMap: "MapServer",
            neededDomains: ["ZONE_ID"],
          });
          const [parcelsIntersectRes] = await Promise.all([parcelsIntersect]);

          const res = await api.post("lookups", {
            lookupsArray: ["Permissions", "Roles"],
          });
          const serverLookupsAsArr = res.data.data.map((item) => ({
            [item.id]: item.list,
          }));
          const serverLookups = {};
          serverLookupsAsArr.forEach((lookup) => {
            const lookupEntry = Object.entries(lookup);
            const [key, objValue] = lookupEntry[0];
            serverLookups[key] = objValue;
          });
          const RolesNoPeople = serverLookups.Roles.filter(
            (item) => item.name !== "people"
          );

          dispatch({
            type: "SET_DATA",
            payload: {
              zonesLookup: mutateObjectToWorkWithReactSelect(
                parcelsIntersectRes.domains.ZONE_ID
              ),
              // usersRoles,
              // usersRolesNoPeople,
              ...serverLookups,
              RolesNoPeople,
            },
          });
        }
      } catch (error) {
        ErrorHandelFunctionApp(error)
      }
    })();
  }, [auth.token, esriUrls.Parcels_Intersect]);

  return (
    <lookupsContext.Provider value={value}>{children}</lookupsContext.Provider>
  );
}

LookupsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// custom hook for using context
function useLookups() {
  const context = useContext(lookupsContext);

  if (!context) {
    throw new Error(
      "useLookups should be used inside the LookupsContextProvider."
    );
  }

  return context;
}

export { LookupsContextProvider, useLookups };
